import React, { useState, useEffect, useCallback } from "react"
import { db } from "../../../utils/firebase"
import moment from "moment"

// UI imports
import Button from "../../../components/ui/button/button"
import Table from "../../../components/structure/table/table"

// Export helper
import { exportSheetsToExcel } from "../_utils/export"

/**
 * Load a CMS collection into a { id, name } list sorted alphabetically by name
 * (case-insensitive). The website CMS docs store the display name as `title`.
 */
const loadCollection = async (collection) => {
	const snap = await db.collection(collection).get()
	return snap.docs
		.map((doc) => ({ id: doc.id, name: doc.data().title || "(Untitled)" }))
		.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
}

/**
 * "CMS holidays & courses" tab - exports the website CMS catalogue
 * (CMS_holidays / CMS_courses) to a spreadsheet. Just two columns, Name and ID,
 * no joins or counts, sorted alphabetically. Both lists download as a single
 * workbook with a tab each.
 */
export default function CmsCatalogue() {
	const [loading, setLoading] = useState(false)
	const [loaded, setLoaded] = useState(false)
	const [holidaysList, setHolidaysList] = useState([])
	const [coursesList, setCoursesList] = useState([])

	/**
	 * Load both CMS collections in parallel.
	 */
	const loadCatalogue = useCallback(async () => {
		setLoading(true)

		try {
			const [holidays, courses] = await Promise.all([loadCollection("CMS_holidays"), loadCollection("CMS_courses")])
			setHolidaysList(holidays)
			setCoursesList(courses)
			setLoaded(true)
		} catch (error) {
			console.error("Error loading CMS catalogue:", error)
		} finally {
			setLoading(false)
		}
	}, [])

	// Load once when the tab mounts
	useEffect(() => {
		loadCatalogue()
	}, [loadCatalogue])

	// Section config so the two tables render from a single template
	const sections = [
		{ key: "Holidays", noun: "holiday", list: holidaysList },
		{ key: "Courses", noun: "course", list: coursesList },
	]

	// Total rows across both lists (drives the export button state)
	const totalRows = holidaysList.length + coursesList.length

	/**
	 * Export both lists into a single XLSX workbook - one tab per list
	 * (Holidays / Courses), each with Name and ID columns.
	 */
	const exportAll = () => {
		if (!loaded || totalRows === 0) return

		const headers = ["Name", "ID"]
		const toRows = (list) => list.map((item) => ({ Name: item.name, ID: item.id }))
		const sheets = sections.map(({ key, list }) => ({ name: key, rows: toRows(list), headers }))

		exportSheetsToExcel(sheets, `cms_catalogue_${moment().format("YYYY-MM-DD")}`)
	}

	return (
		<>
			<p className="db-exports-intro">
				Export the website CMS holidays and CMS courses. Each list shows just the name and ID, sorted alphabetically, and downloads as a single spreadsheet with a tab each for holidays and courses.
			</p>

			<div className="page-filters db-exports-filters">
				<Button
					filters={true}
					disabled={loading}
					label="Refresh"
					loadingText="Loading..."
					loading={loading}
					onClick={() => loadCatalogue()}
				/>

				<Button
					filters={true}
					disabled={!loaded || totalRows === 0}
					label="Export to XLSX (2 tabs)"
					onClick={() => exportAll()}
				/>
			</div>

			{loaded && !loading && (
				<p className="db-exports-summary">
					Loaded <strong>{holidaysList.length.toLocaleString()}</strong> holiday{holidaysList.length === 1 ? "" : "s"} and <strong>{coursesList.length.toLocaleString()}</strong> course
					{coursesList.length === 1 ? "" : "s"}.
				</p>
			)}

			{/* Result sections */}
			{sections.map(({ key, noun, list }) => (
				<div
					key={key}
					className="db-exports-section">
					<div className="db-exports-section-header">
						<h2>
							{key} <span className="db-exports-count">({list.length.toLocaleString()} {noun}{list.length === 1 ? "" : "s"})</span>
						</h2>
					</div>

					<Table
						className="db-exports-table"
						headings={["Name", "ID"]}
						noResults={list.length === 0}
						noResultsMessage={loading ? "Loading..." : `No ${noun}s found`}>
						{list.map((item) => (
							<tr key={item.id}>
								<td>{item.name}</td>
								<td className="db-exports-id">{item.id}</td>
							</tr>
						))}
					</Table>
				</div>
			))}
		</>
	)
}
